import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { RunStep } from '@app/shared';
import {
  Button,
  Cluster,
  CodeBlock,
  Disclosure,
  Icon,
  Icons,
  Stack,
  Text,
  Timeline,
  TimelineItem,
} from '../ui';
import { api } from '../lib/api';
import { RunStatusBadge } from './RunStatusBadge';
import { KIND_LABEL, summarizeStep } from './summarizeStep';

/**
 * The step-by-step transcript (§14): model turns, tool calls with args/results,
 * approvals, wakes — rendered on the {@link Timeline} rail. Live via SSE — the
 * operations stream invalidates the run-detail query as steps land, refetching the
 * first page; this component then keyset-paginates the rest on demand.
 *
 * This is the power-user "advanced" view; it lives on its own route
 * (`/app/runs/$runId/transcript`, issue #120) so the run page can lead with Outputs.
 *
 * #148: every step renders COLLAPSED by default (a {@link Disclosure} whose body is
 * unmounted while closed), with an informative header (kind + tool + one-line summary
 * via `summarizeStep`) so a long transcript is scannable without expanding each turn.
 */

/** Per-kind marker tone on the timeline rail so the eye can group turn types. */
type MarkerTone = 'action' | 'brand' | 'warning' | 'success' | 'muted';
const KIND_TONE: Record<RunStep['kind'], MarkerTone> = {
  model_turn: 'action',
  tool_call: 'brand',
  approval: 'warning',
  wake: 'success',
  system: 'muted',
};

/** One entry in a `model_turn`'s `payload.toolUses` — the model's decided tool call. */
interface ToolUse {
  name: string;
  input?: unknown;
}

function isToolUse(v: unknown): v is ToolUse {
  return typeof v === 'object' && v !== null && typeof (v as { name?: unknown }).name === 'string';
}

/** Compact "1.2k" style token count for the muted per-turn caption. */
function formatTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

/**
 * #139: render a `model_turn` by WHAT IT CONTAINS rather than a bare uuid + JSON:
 * a decision line ("Decided to call: `email.read`, …") with each tool's input inline,
 * "Reasoning / narration" prose, extended `thinking` in a COLLAPSED disclosure
 * (unmounted while closed), and a muted per-turn token caption.
 */
function ModelTurn({ step }: { step: RunStep }) {
  const payload = step.payload as {
    text?: unknown;
    thinking?: unknown;
    toolUses?: unknown;
    tokensIn?: unknown;
    tokensOut?: unknown;
  };
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const toolUses = Array.isArray(payload.toolUses) ? payload.toolUses.filter(isToolUse) : [];
  const text = typeof payload.text === 'string' ? payload.text : undefined;
  const thinking = typeof payload.thinking === 'string' ? payload.thinking : undefined;
  const tokensIn = typeof payload.tokensIn === 'number' ? payload.tokensIn : undefined;
  const tokensOut = typeof payload.tokensOut === 'number' ? payload.tokensOut : undefined;

  if (toolUses.length === 0 && text === undefined && thinking === undefined) {
    return <CodeBlock value={step.payload} />;
  }

  const withInput = toolUses.filter((tu) => tu.input !== undefined && tu.input !== null);

  return (
    <Stack gap="2">
      {toolUses.length > 0 && (
        <Stack gap="1" data-testid="model-turn-decision">
          <Text size="sm" tone="secondary">
            Decided to call:{' '}
            {toolUses.map((tu, i) => (
              <Text as="span" key={`${tu.name}-${i}`} size="sm" mono tone="primary">
                {i > 0 ? ', ' : ''}
                {tu.name}
              </Text>
            ))}
          </Text>
          {withInput.map((tu, i) => (
            <CodeBlock key={`in-${tu.name}-${i}`} size="sm" value={tu.input} />
          ))}
        </Stack>
      )}

      {text !== undefined && (
        <Stack gap="1">
          {toolUses.length > 0 && (
            <Text size="xs" tone="muted">
              Reasoning / narration
            </Text>
          )}
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {text}
          </Text>
        </Stack>
      )}

      {thinking !== undefined && (
        <Disclosure
          open={thinkingOpen}
          onToggle={() => setThinkingOpen((o) => !o)}
          data-testid="model-turn-thinking"
          summary={
            <Text as="span" size="xs" tone="muted">
              Thinking
            </Text>
          }
        >
          <Text size="sm" tone="secondary" style={{ whiteSpace: 'pre-wrap' }}>
            {thinking}
          </Text>
        </Disclosure>
      )}

      {(tokensIn !== undefined || tokensOut !== undefined) && (
        <Text size="xs" tone="muted">
          {'· '}
          {tokensIn !== undefined ? `${formatTokens(tokensIn)} in` : ''}
          {tokensIn !== undefined && tokensOut !== undefined ? ' / ' : ''}
          {tokensOut !== undefined ? `${formatTokens(tokensOut)} out` : ''}
        </Text>
      )}
    </Stack>
  );
}

function StepText({ step }: { step: RunStep }) {
  if (step.kind === 'model_turn') return <ModelTurn step={step} />;
  const payload = step.payload as { text?: unknown };
  if (typeof payload.text === 'string') {
    return (
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {payload.text}
      </Text>
    );
  }
  return <CodeBlock value={step.payload} />;
}

/**
 * Transcript with keyset pagination (§14). The run-detail fetch returns the FIRST page
 * of steps + a `nextStepCursor`; the rest load on demand so a long run's transcript
 * never arrives in one response. When the base query refetches (SSE → new steps), we
 * reset back to the fresh first page.
 */
export function RunTranscript({
  workspaceId,
  runId,
  initialSteps,
  initialCursor,
}: {
  workspaceId: string;
  runId: string;
  initialSteps: RunStep[];
  initialCursor: number | null;
}) {
  const [extra, setExtra] = useState<RunStep[]>([]);
  const [cursor, setCursor] = useState<number | null>(initialCursor);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    setExtra([]);
    setCursor(initialCursor);
  }, [initialSteps, initialCursor]);

  const loadMore = useMutation({
    mutationFn: () => api.getRunSteps(workspaceId, runId, { after: cursor ?? undefined }),
    onSuccess: (page) => {
      setExtra((prev) => [...prev, ...page.steps]);
      setCursor(page.nextStepCursor);
    },
  });

  const steps = [...initialSteps, ...extra];
  const allExpanded = steps.length > 0 && steps.every((s) => expanded.has(s.stepNo));

  const toggleStep = (stepNo: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(stepNo)) next.delete(stepNo);
      else next.add(stepNo);
      return next;
    });

  const expandAll = () => setExpanded(new Set(steps.map((s) => s.stepNo)));
  const collapseAll = () => setExpanded(new Set());

  if (steps.length === 0) {
    return <Text tone="secondary">No steps yet.</Text>;
  }

  return (
    <Stack gap="3">
      <Cluster justify="end">
        <Button
          variant="ghost"
          size="sm"
          iconStart={<Icon as={allExpanded ? Icons.ChevronsDownUp : Icons.ChevronsUpDown} size="sm" />}
          onClick={allExpanded ? collapseAll : expandAll}
          data-testid="toggle-all-steps"
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </Button>
      </Cluster>

      <Timeline markerOffset="0.75rem">
        {steps.map((step) => (
          <TimelineItem
            key={step.stepNo}
            marker={<Icon as={Icons.Circle} size="xs" tone={KIND_TONE[step.kind]} strokeWidth={5} />}
          >
            <TranscriptStep
              step={step}
              expanded={expanded.has(step.stepNo)}
              onToggle={() => toggleStep(step.stepNo)}
            />
          </TimelineItem>
        ))}
      </Timeline>

      {cursor !== null && (
        <Cluster>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => loadMore.mutate()}
            loading={loadMore.isPending}
            loadingText="Loading…"
            data-testid="load-more-steps"
          >
            Load more steps
          </Button>
        </Cluster>
      )}
    </Stack>
  );
}

function TranscriptStep({
  step,
  expanded,
  onToggle,
}: {
  step: RunStep;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { title, toolLabel, summary } = summarizeStep(step);
  return (
    <Disclosure
      open={expanded}
      onToggle={onToggle}
      data-testid="transcript-step"
      data-kind={step.kind}
      bodyTestId="step-body"
      summary={
        <Cluster gap="2" wrap={false}>
          <Text as="span" size="xs" tone="muted" mono>
            #{step.stepNo}
          </Text>
          <Text as="span" size="sm" weight="semibold">
            {title || KIND_LABEL[step.kind]}
          </Text>
          {toolLabel && (
            <Text as="span" size="xs" tone="secondary" mono truncate>
              {toolLabel}
            </Text>
          )}
          {step.toolStatus && (
            <RunStatusBadge status={step.toolStatus === 'ok' ? 'ok' : step.toolStatus} size="sm" />
          )}
          {summary && (
            <Text as="span" size="sm" tone="secondary" truncate data-testid="step-summary">
              {summary}
            </Text>
          )}
          <Text
            as="span"
            size="xs"
            tone="muted"
            title={new Date(step.createdAt).toLocaleString()}
            data-testid="step-time"
            style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
          >
            {new Date(step.createdAt).toLocaleTimeString()}
          </Text>
        </Cluster>
      }
    >
      <Stack gap="2" style={{ paddingTop: '0.5rem' }}>
        <StepText step={step} />
      </Stack>
    </Disclosure>
  );
}
